export const VIDEO_CHAT_QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏", "✅"];
export const VIDEO_CHAT_MEDIA_TYPES = ["image", "video", "audio", "file"];
export const VIDEO_CHAT_MAX_FILE_BYTES = 40 * 1024 * 1024;
export const VIDEO_CHAT_ACCEPT =
  "image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar";

const DEFAULT_SNIPPET_LABELS = {
  image: "📷 Imagem",
  video: "🎬 Vídeo",
  audio: "🎤 Áudio",
  file: "📎 Arquivo",
};

export function videoChatMediaTypeFromFile(file) {
  const mime = String(file?.type || "").toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return "file";
}

export function pickVideoChatMedia(raw = {}) {
  const explicit = String(raw.messageType || raw.message_type || "").trim().toLowerCase();
  const mediaUrl = String(
    raw.mediaUrl ||
      raw.media_url ||
      raw.imageUrl ||
      raw.image_url ||
      raw.videoUrl ||
      raw.video_url ||
      raw.audioUrl ||
      raw.audio_url ||
      raw.fileUrl ||
      raw.file_url ||
      ""
  ).trim();
  let messageType = VIDEO_CHAT_MEDIA_TYPES.includes(explicit) ? explicit : "text";
  if (mediaUrl && messageType === "text") {
    if (raw.imageUrl || raw.image_url) messageType = "image";
    else if (raw.videoUrl || raw.video_url) messageType = "video";
    else if (raw.audioUrl || raw.audio_url) messageType = "audio";
    else messageType = "file";
  }
  if (!mediaUrl) messageType = explicit === "text" || !explicit ? "text" : messageType;
  if (!mediaUrl) {
    return {
      messageType: "text",
      mediaUrl: "",
      fileName: String(raw.fileName || raw.file_name || "").trim(),
    };
  }
  return {
    messageType,
    mediaUrl,
    fileName: String(raw.fileName || raw.file_name || "").trim(),
  };
}

export function videoChatHasMedia(msg) {
  const media = pickVideoChatMedia(msg || {});
  return Boolean(media.mediaUrl) && VIDEO_CHAT_MEDIA_TYPES.includes(media.messageType);
}

export function videoChatSnippet(msg, { maxLen = 120, labels } = {}) {
  const copy = { ...DEFAULT_SNIPPET_LABELS, ...(labels || {}) };
  const media = pickVideoChatMedia(msg || {});
  if (media.mediaUrl) {
    if (media.messageType === "image") return copy.image;
    if (media.messageType === "video") return copy.video;
    if (media.messageType === "audio") return copy.audio;
    return media.fileName ? `📎 ${media.fileName}` : copy.file;
  }
  const text = String(msg?.body || msg?.content || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
}

export function groupVideoChatReactions(reactions = []) {
  const map = new Map();
  for (const raw of reactions || []) {
    const emoji = String(raw?.emoji || "").trim();
    const identity = String(raw?.identity || raw?.senderIdentity || "").trim();
    if (!emoji || !identity) continue;
    if (!map.has(emoji)) map.set(emoji, []);
    map.get(emoji).push({
      identity,
      userName: String(raw?.senderName || raw?.userName || "").trim(),
    });
  }
  return [...map.entries()].map(([emoji, users]) => ({
    emoji,
    users,
    count: users.length,
  }));
}

export function toggleVideoChatReactions(reactions, { identity, senderName, emoji } = {}) {
  const mine = String(identity || "").trim();
  const nextEmoji = String(emoji || "").trim();
  const list = Array.isArray(reactions) ? reactions.filter(Boolean) : [];
  if (!mine || !nextEmoji) return list;
  const idx = list.findIndex((row) => String(row.identity || row.senderIdentity || "") === mine);
  if (idx >= 0) {
    if (String(list[idx].emoji || "") === nextEmoji) {
      return list.filter((_, i) => i !== idx);
    }
    const copy = [...list];
    copy[idx] = { identity: mine, senderName: senderName || list[idx].senderName || "", emoji: nextEmoji };
    return copy;
  }
  return [...list, { identity: mine, senderName: senderName || "", emoji: nextEmoji }];
}

export function videoChatLastReadStorageKey({ token, identity } = {}) {
  const room = String(token || "").trim();
  const who = String(identity || "").trim();
  if (!room) return "";
  return `xpro.videoChat.lastRead:${room}:${who || "anon"}`;
}

export function readVideoChatLastRead(storageKey) {
  if (!storageKey || typeof localStorage === "undefined") return 0;
  try {
    const id = Number(localStorage.getItem(storageKey));
    return Number.isFinite(id) && id > 0 ? Math.floor(id) : 0;
  } catch {
    return 0;
  }
}

export function writeVideoChatLastRead(storageKey, messageId) {
  const id = Number(messageId);
  if (!storageKey || !Number.isFinite(id) || id <= 0 || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(storageKey, String(Math.floor(id)));
  } catch {
    /* ignore quota / private mode */
  }
}

export function maxVideoChatMessageId(messages = []) {
  let max = 0;
  for (const row of messages || []) {
    const id = Number(row?.id);
    if (Number.isFinite(id) && id > max) max = id;
  }
  return max;
}

export function countVideoChatUnread(messages = [], { lastReadId = 0, localIdentity = "" } = {}) {
  const read = Number(lastReadId) || 0;
  const mine = String(localIdentity || "").trim();
  let count = 0;
  for (const row of messages || []) {
    const id = Number(row?.id);
    if (!Number.isFinite(id) || id <= 0 || id <= read) continue;
    if (row?.pending) continue;
    if (mine && String(row.senderIdentity || "") === mine) continue;
    count += 1;
  }
  return count;
}

export function videoChatNumericId(msg) {
  const id = Number(msg?.id ?? msg?.messageId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || ""));
}

function sameVideoChatContent(a, b) {
  if (!a || !b) return false;
  if (String(a.senderIdentity || "") !== String(b.senderIdentity || "")) return false;
  if (String(a.body || "").trim() !== String(b.body || "").trim()) return false;
  const fileA = String(a.fileName || "");
  const fileB = String(b.fileName || "");
  if (fileA && fileB && fileA !== fileB) return false;
  const httpA = isHttpUrl(a.mediaUrl) ? String(a.mediaUrl) : "";
  const httpB = isHttpUrl(b.mediaUrl) ? String(b.mediaUrl) : "";
  if (httpA && httpB && httpA !== httpB) return false;
  return true;
}

export function combineVideoChatMessages(prev, next) {
  if (!prev) return next || null;
  if (!next) return prev;
  return {
    ...prev,
    ...next,
    pending: Boolean(prev.pending) && Boolean(next.pending),
    clientId: next.clientId || prev.clientId || null,
    id: videoChatNumericId(next) || videoChatNumericId(prev),
    mediaUrl: isHttpUrl(next.mediaUrl)
      ? next.mediaUrl
      : isHttpUrl(prev.mediaUrl)
        ? prev.mediaUrl
        : next.mediaUrl || prev.mediaUrl || null,
    fileName: next.fileName || prev.fileName || null,
    messageType: next.messageType || prev.messageType || "text",
    reactions: Array.isArray(next.reactions)
      ? next.reactions
      : Array.isArray(prev.reactions)
        ? prev.reactions
        : [],
    replyToId: next.replyToId ?? prev.replyToId ?? null,
    replyPreview: next.replyPreview || prev.replyPreview || null,
    mentions: Array.isArray(next.mentions) ? next.mentions : prev.mentions,
    mentionAll: Boolean(next.mentionAll || prev.mentionAll),
  };
}

export function videoChatMessagesMatch(a, b) {
  if (!a || !b) return false;
  const idA = videoChatNumericId(a);
  const idB = videoChatNumericId(b);
  if (idA && idB) return idA === idB;
  if (a.clientId && b.clientId && String(a.clientId) === String(b.clientId)) return true;
  if (idA && !idB && sameVideoChatContent(a, b)) return true;
  if (idB && !idA && sameVideoChatContent(a, b)) return true;
  return false;
}

function sortVideoChatMessages(list) {
  return [...list].sort((a, b) => {
    const aid = videoChatNumericId(a) || 0;
    const bid = videoChatNumericId(b) || 0;
    if (aid && bid && aid !== bid) return aid - bid;
    return String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
  });
}

export function mergeVideoChatMessageLists(prev = [], incoming = []) {
  const list = [];
  const indexById = new Map();
  const indexByClient = new Map();

  const reindex = (idx, msg) => {
    const id = videoChatNumericId(msg);
    const clientId = msg?.clientId ? String(msg.clientId) : "";
    if (id) indexById.set(id, idx);
    if (clientId) indexByClient.set(clientId, idx);
  };

  const findIndex = (msg) => {
    const id = videoChatNumericId(msg);
    const clientId = msg?.clientId ? String(msg.clientId) : "";
    if (id && indexById.has(id)) return indexById.get(id);
    if (clientId && indexByClient.has(clientId)) return indexByClient.get(clientId);
    // Só une pendente (sem id) com a cópia já persistida (com id).
    // Nunca o contrário: um segundo "ok" local não pode sumir no "ok" antigo.
    if (!id) return -1;
    return list.findIndex((row) => {
      if (videoChatNumericId(row)) return false;
      return sameVideoChatContent(row, msg);
    });
  };

  const put = (msg) => {
    if (!msg) return;
    const idx = findIndex(msg);
    if (idx >= 0) {
      const merged = combineVideoChatMessages(list[idx], msg);
      list[idx] = merged;
      reindex(idx, merged);
      return;
    }
    const at = list.length;
    list.push(msg);
    reindex(at, msg);
  };

  (Array.isArray(prev) ? prev : []).forEach(put);
  (Array.isArray(incoming) ? incoming : []).forEach(put);
  return sortVideoChatMessages(list);
}
